/**
 * TaskModalContext — a tiny global store for "which task is open in the shared
 * task modal". Opening a task from ANY productivity page (the person lane, a
 * deep-mode chat message, initiatives, ideas, …) calls openTask(id), which makes
 * the single app-root <TaskModal/> show that task in-place as an overlay. No
 * navigation to /tasks — the user stays on the page they're on.
 *
 * State only; nothing is persisted.
 */
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

interface TaskModalContextValue {
  // The task currently shown in the shared modal, or null when closed.
  openTaskId: number | null;
  // Open the shared task modal for a given task id (in-place overlay).
  openTask: (id: number) => void;
  // Close the shared task modal.
  closeTask: () => void;
}

const TaskModalContext = createContext<TaskModalContextValue | undefined>(undefined);

export const TaskModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [openTaskId, setOpenTaskId] = useState<number | null>(null);

  const openTask = useCallback((id: number) => setOpenTaskId(id), []);
  const closeTask = useCallback(() => setOpenTaskId(null), []);

  const value = useMemo(
    () => ({ openTaskId, openTask, closeTask }),
    [openTaskId, openTask, closeTask]
  );

  return <TaskModalContext.Provider value={value}>{children}</TaskModalContext.Provider>;
};

export const useTaskModal = (): TaskModalContextValue => {
  const ctx = useContext(TaskModalContext);
  if (!ctx) {
    throw new Error('useTaskModal must be used within a TaskModalProvider');
  }
  return ctx;
};
